import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import {
  applySuggestion,
  mergeAssemblyInstruction,
  mergeSizingPatch,
  parseSalesDescription,
  pickLongDescription,
  serializeSalesDescription,
  splitAssemblyInstruction,
  type AiCandidate,
  type ChatEditorPatch,
  type LineInput,
  type LineStatus,
  type NewArticle,
  type Product,
  type ProductLine,
  type ProductWithLines,
  type SizingInput,
  type SupplierProduct,
  type UpdateProductBody,
  type ValidationIssue,
} from '@klingerator/shared';
import { productService } from '@/services/productService';
import { unit4Service } from '@/services/unit4Service';
import { extractErrorMessage } from '@/services/api';
import { toastService } from '@/services/ToastService';

/** Bewerkbare kopie van een productregel (id null = nieuw, nog niet opgeslagen). */
export interface EditableLine {
  /** Stabiele sleutel voor v-for (ook voor nieuwe regels zonder id) */
  key: string;
  id: number | null;
  description: string;
  unit4_product_code: string | null;
  unit4_description: string | null;
  unit_code: string | null;
  qty_in_composition: number;
  composition_per: number;
  percentage_loss: number;
  child_product_id: number | null;
  child_product: ProductLine['child_product'];
  is_composite: boolean;
  /** Nieuw artikel: nog niet in Unit4, wordt bij goedkeuring aangemaakt (BugHerd #1468) */
  new_article: NewArticle | null;
  status: LineStatus;
  ai_query: string | null;
  ai_candidates: AiCandidate[] | null;
  validation_errors: ValidationIssue[] | null;
  notes: string | null;
  line_number: number | null;
}

/** Kostprijs uit het formulier: leeg = sjabloon volgen, komma of punt als decimaalteken. */
export function parseCostPrice(value: string): number | null {
  return value.trim() ? Number(value.replace(',', '.')) : null;
}
export function costPriceProblem(value: string): string | null {
  const n = parseCostPrice(value);
  return n !== null && (!Number.isFinite(n) || n < 0) ? 'Vul een bedrag van 0 of hoger in' : null;
}

/** Productvelden die via PUT /products/:id bewerkbaar zijn. */
export interface ProductFormFields {
  name: string;
  long_description_nl: string;
  long_description_en: string;
  assembly_instruction: string;
  assembly_warehouse_code: string;
  use_partial_relocation: '' | 'J' | 'N';
  use_component_partial: '' | 'J' | 'N';
  partial_relocation_minimum: string;
  apply_sub_assembly_end_product: '' | 'J' | 'N';
  apply_sub_assembly_component: '' | 'J' | 'N';
  /** Overrides van sjabloonvelden (leeg = sjabloon volgen) */
  journal_entry_code: string;
  warehouse_code: string;
  location_code: string;
  ean_code: string;
  cost_price: string;
  /** Alleen in de dialoog van een nieuw artikel (BugHerd #1469); een product heeft dit veld niet */
  supplier_product: SupplierProduct | null;
  search_name: string;
  unit_code: string;
  template_product_code: string;
  template_id: number | null;
  proposed_product_code: string;
  notes: string;
  /** Opt-in actuator-sizing-invoer; null = geen sizing op dit product */
  sizing_input: SizingInput | null;
}

export function emptyProductForm(): ProductFormFields {
  return {
    name: '',
    long_description_nl: '',
    long_description_en: '',
    assembly_instruction: '',
    assembly_warehouse_code: '',
    use_partial_relocation: '',
    use_component_partial: '',
    partial_relocation_minimum: '',
    apply_sub_assembly_end_product: '',
    apply_sub_assembly_component: '',
    journal_entry_code: '',
    warehouse_code: '',
    location_code: '',
    ean_code: '',
    cost_price: '',
    supplier_product: null,
    search_name: '',
    unit_code: '',
    template_product_code: '',
    template_id: null,
    proposed_product_code: '',
    notes: '',
    sizing_input: null,
  };
}

export function productFormErrors(form: ProductFormFields): Partial<Record<keyof ProductFormFields, string>> {
  const e: Partial<Record<keyof ProductFormFields, string>> = {};
  if (!form.name.trim()) e.name = 'Omschrijving is verplicht';
  if (form.search_name.length > 10) e.search_name = 'Maximaal 10 tekens';
  if (!form.unit_code.trim()) e.unit_code = 'Eenheid is verplicht';
  const costProblem = costPriceProblem(form.cost_price);
  if (costProblem) e.cost_price = costProblem;
  return e;
}

export interface LineValidationError {
  index: number;
  field: keyof EditableLine;
  message: string;
}

let keySeq = 0;
const newKey = () => `n${++keySeq}`;

function toEditable(l: ProductLine): EditableLine {
  return {
    key: `l${l.id}`,
    id: l.id,
    description: l.description ?? '',
    unit4_product_code: l.unit4_product_code ?? null,
    unit4_description: l.unit4_description ?? null,
    unit_code: l.unit_code ?? null,
    qty_in_composition: Number(l.qty_in_composition),
    composition_per: Number(l.composition_per ?? 1),
    percentage_loss: Number(l.percentage_loss ?? 0),
    child_product_id: l.child_product_id ?? null,
    child_product: l.child_product ?? null,
    is_composite: !!l.is_composite || !!l.child_product_id,
    new_article: l.new_article ?? null,
    status: l.status,
    ai_query: l.ai_query ?? null,
    ai_candidates: l.ai_candidates ?? null,
    validation_errors: l.validation_errors ?? null,
    notes: l.notes ?? null,
    line_number: l.line_number ?? null,
  };
}

export function toForm(p: Product): ProductFormFields {
  return {
    name: p.name ?? '',
    long_description_nl: p.long_description_nl ?? '',
    long_description_en: p.long_description_en ?? '',
    assembly_instruction: p.assembly_instruction ?? '',
    assembly_warehouse_code: p.assembly_warehouse_code ?? '',
    use_partial_relocation: (p.use_partial_relocation ?? '') as '' | 'J' | 'N',
    use_component_partial: (p.use_component_partial ?? '') as '' | 'J' | 'N',
    partial_relocation_minimum:
      p.partial_relocation_minimum != null ? String(p.partial_relocation_minimum) : '',
    apply_sub_assembly_end_product: (p.apply_sub_assembly_end_product ?? '') as '' | 'J' | 'N',
    apply_sub_assembly_component: (p.apply_sub_assembly_component ?? '') as '' | 'J' | 'N',
    journal_entry_code: p.journal_entry_code ?? '',
    warehouse_code: p.warehouse_code ?? '',
    location_code: p.location_code ?? '',
    ean_code: p.ean_code ?? '',
    cost_price: p.cost_price != null ? String(p.cost_price) : '',
    supplier_product: null,
    search_name: p.search_name ?? '',
    unit_code: p.unit_code ?? '',
    template_product_code: p.template_product_code ?? '',
    template_id: p.template_id ?? null,
    proposed_product_code: p.proposed_product_code ?? '',
    notes: p.notes ?? '',
    sizing_input: p.sizing_input ?? null,
  };
}

const nullIfEmpty = (v: string | null | undefined) => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

/** Formuliervelden in de vorm van de API (lege velden = NULL). */
export function formToBody(form: ProductFormFields): Omit<UpdateProductBody, 'version'> & { name: string } {
  return {
    name: form.name.trim(),
    search_name: nullIfEmpty(form.search_name)?.toUpperCase() ?? null,
    unit_code: form.unit_code.trim().toUpperCase(),
    template_product_code: nullIfEmpty(form.template_product_code),
    template_id: form.template_id,
    long_description_nl: nullIfEmpty(form.long_description_nl),
    long_description_en: nullIfEmpty(form.long_description_en),
    assembly_instruction: nullIfEmpty(form.assembly_instruction),
    assembly_warehouse_code: nullIfEmpty(form.assembly_warehouse_code),
    use_partial_relocation: form.use_partial_relocation || null,
    use_component_partial: form.use_component_partial || null,
    partial_relocation_minimum: form.partial_relocation_minimum.trim()
      ? Number(form.partial_relocation_minimum.replace(',', '.'))
      : null,
    apply_sub_assembly_end_product: form.apply_sub_assembly_end_product || null,
    apply_sub_assembly_component: form.apply_sub_assembly_component || null,
    journal_entry_code: nullIfEmpty(form.journal_entry_code),
    warehouse_code: nullIfEmpty(form.warehouse_code),
    location_code: nullIfEmpty(form.location_code),
    ean_code: nullIfEmpty(form.ean_code),
    cost_price: parseCostPrice(form.cost_price),
    proposed_product_code: nullIfEmpty(form.proposed_product_code),
    notes: nullIfEmpty(form.notes),
    sizing_input: form.sizing_input,
  };
}

/** Zet een bewerkbare regel om in de body voor PUT /products/:id/lines. */
export function toLineInput(l: EditableLine, index: number): LineInput {
  return {
    id: l.id ?? null,
    sort_order: index + 1,
    description: l.description.trim(),
    unit4_product_code: nullIfEmpty(l.unit4_product_code),
    unit_code: nullIfEmpty(l.unit_code)?.toUpperCase() ?? null,
    qty_in_composition: Number(l.qty_in_composition),
    composition_per: Number(l.composition_per) || 1,
    percentage_loss: Number(l.percentage_loss) || 0,
    child_product_id: l.child_product_id ?? null,
    new_article: l.new_article,
    ai_candidates: l.ai_candidates ?? null,
    ai_query: l.ai_query ?? null,
    notes: nullIfEmpty(l.notes),
  };
}

function isVersionMismatch(e: unknown): boolean {
  return (
    axios.isAxiosError(e) &&
    e.response?.status === 409 &&
    (e.response.data as any)?.error?.code === 'VERSION_MISMATCH'
  );
}

/**
 * Editor-store voor één product: laadt product+regels, houdt een bewerkbare kopie bij,
 * berekent `dirty` en slaat op via PUT /products/:id (velden) en PUT /products/:id/lines (bulk).
 */
export const useProductEditorStore = defineStore('productEditor', () => {
  const product = ref<ProductWithLines | null>(null);
  const lines = ref<EditableLine[]>([]);
  const form = reactive<ProductFormFields>(emptyProductForm());
  const loading = ref(false);
  const saving = ref(false);
  const loadError = ref<string | null>(null);
  /** Gezet als de server een nieuwere versie heeft (409 VERSION_MISMATCH) — UI toont herlaad-banner. */
  const conflictVersion = ref<number | null>(null);
  const lastSavedAt = ref<Date | null>(null);

  const productId = computed(() => product.value?.id ?? null);
  const version = computed(() => product.value?.version ?? 0);

  /** Origineel (server-staat) in dezelfde vorm als `lines`, voor dirty-vergelijking. */
  const original = computed<EditableLine[]>(() => (product.value?.lines ?? []).map(toEditable));
  const originalForm = computed<ProductFormFields | null>(() =>
    product.value ? toForm(product.value) : null,
  );

  const linesDirty = computed(() => {
    const a = JSON.stringify(lines.value.map(toLineInput));
    const b = JSON.stringify(original.value.map(toLineInput));
    return a !== b;
  });
  const productDirty = computed(() => {
    if (!originalForm.value) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm.value);
  });
  const dirty = computed(() => linesDirty.value || productDirty.value);

  const validationErrors = computed<LineValidationError[]>(() => {
    const errs: LineValidationError[] = [];
    lines.value.forEach((l, i) => {
      if (!l.description.trim())
        errs.push({ index: i, field: 'description', message: 'Omschrijving is verplicht' });
      if (!(Number(l.qty_in_composition) > 0))
        errs.push({ index: i, field: 'qty_in_composition', message: 'Aantal moet groter dan 0 zijn' });
      if (!(Number(l.composition_per) > 0))
        errs.push({ index: i, field: 'composition_per', message: 'Per moet groter dan 0 zijn' });
      const loss = Number(l.percentage_loss);
      if (loss < 0 || loss >= 100)
        errs.push({ index: i, field: 'percentage_loss', message: 'Verlies moet tussen 0 en 100% liggen' });
    });
    return errs;
  });
  const formErrors = computed(() => productFormErrors(form));
  const isValid = computed(
    () => validationErrors.value.length === 0 && Object.keys(formErrors.value).length === 0,
  );

  const stats = computed(() => ({
    total: lines.value.length,
    provisional: lines.value.filter((l) => l.status === 'provisional').length,
    invalid: lines.value.filter((l) => l.status === 'invalid').length,
    composite: lines.value.filter((l) => l.is_composite).length,
  }));

  // ---------- Laden / resetten ----------

  function applyServerState(p: ProductWithLines) {
    product.value = p;
    lines.value = p.lines.map(toEditable);
    Object.assign(form, toForm(p));
    conflictVersion.value = null;
  }

  async function load(id: number, opts: { silent?: boolean } = {}): Promise<void> {
    loading.value = true;
    loadError.value = null;
    try {
      const p = await productService.get(id, { silent: opts.silent, noToast: true });
      applyServerState(p);
    } catch (e) {
      loadError.value = extractErrorMessage(e, 'Product kon niet worden geladen');
      product.value = null;
      lines.value = [];
    } finally {
      loading.value = false;
    }
  }

  /** Server-staat verversen zonder lokale wijzigingen te verliezen (dirty blijft = lokaal ≠ server). */
  async function refreshOriginal(): Promise<void> {
    if (!productId.value) return;
    const p = await productService.get(productId.value, { silent: true, noToast: true });
    product.value = p;
    conflictVersion.value = null;
  }

  function discard() {
    if (product.value) applyServerState(product.value);
  }

  function clear() {
    product.value = null;
    lines.value = [];
    conflictVersion.value = null;
    loadError.value = null;
  }

  // ---------- Regels bewerken ----------

  function blankLine(): EditableLine {
    return {
      key: newKey(),
      id: null,
      description: '',
      unit4_product_code: null,
      unit4_description: null,
      unit_code: product.value?.unit_code ?? 'STK',
      qty_in_composition: 1,
      composition_per: 1,
      percentage_loss: 0,
      child_product_id: null,
      child_product: null,
      is_composite: false,
      new_article: null,
      status: 'provisional',
      ai_query: null,
      ai_candidates: null,
      validation_errors: null,
      notes: null,
      line_number: null,
    };
  }

  /** Voegt een lege regel toe ná `afterIndex` (default: onderaan). Retourneert de index van de nieuwe regel. */
  function addLine(afterIndex?: number, preset: Partial<EditableLine> = {}): number {
    const line = { ...blankLine(), ...preset };
    const at =
      afterIndex === undefined || afterIndex < 0 || afterIndex >= lines.value.length
        ? lines.value.length
        : afterIndex + 1;
    lines.value.splice(at, 0, line);
    return at;
  }

  function removeLine(idx: number) {
    if (idx < 0 || idx >= lines.value.length) return;
    lines.value.splice(idx, 1);
  }

  function moveLine(from: number, to: number) {
    const n = lines.value.length;
    if (from === to || from < 0 || from >= n || to < 0 || to >= n) return;
    const [l] = lines.value.splice(from, 1);
    lines.value.splice(to, 0, l);
  }

  function updateLine(idx: number, patch: Partial<EditableLine>) {
    const l = lines.value[idx];
    if (!l) return;
    Object.assign(l, patch);
    // Unit4-code gewist → koppeling weg
    if ('unit4_product_code' in patch && !nullIfEmpty(patch.unit4_product_code)) {
      l.unit4_description = null;
      if (!l.is_composite) l.status = 'provisional';
    }
  }

  function duplicateLine(idx: number): number {
    const src = lines.value[idx];
    if (!src) return -1;
    return addLine(idx, {
      ...JSON.parse(JSON.stringify(src)),
      key: newKey(),
      id: null,
      child_product_id: null,
      child_product: null,
      is_composite: false,
      // een nieuw artikel bestaat maar één keer: de kopie heeft een eigen nummer nodig
      unit4_product_code: src.new_article ? null : src.unit4_product_code,
      status: src.unit4_product_code && !src.new_article ? src.status : 'provisional',
    });
  }

  function updateProduct(patch: Partial<ProductFormFields>) {
    Object.assign(form, patch);
    if (patch.search_name !== undefined) form.search_name = patch.search_name.toUpperCase();
    if (patch.unit_code !== undefined) form.unit_code = patch.unit_code.toUpperCase();
  }

  // ---------- Voorstel van de chat-assistent ----------

  const CHAT_FIELD_LABELS: Record<string, string> = {
    name: 'naam',
    search_name: 'zoeknaam',
    unit_code: 'eenheid',
    proposed_product_code: 'productcode',
    long_description_nl: 'lange omschrijving NL',
    long_description_en: 'lange omschrijving EN',
    assembly_instruction: 'werkplaatsinstructie',
    notes: 'notities',
    journal_entry_code: 'journaalpost',
    warehouse_code: 'magazijn',
    location_code: 'locatie',
    ean_code: 'EAN-code',
    cost_price: 'kostprijs',
    assembly_warehouse_code: 'assemblagemagazijn',
    use_partial_relocation: 'deelverplaatsing',
    use_component_partial: 'deelverplaatsing componenten',
    partial_relocation_minimum: 'minimum deelverplaatsing',
    apply_sub_assembly_end_product: 'sub-assemblage eindproduct',
    apply_sub_assembly_component: 'sub-assemblage component',
    sizing_input: 'sizing-invoer',
  };

  /**
   * Concept-wijzigingen van de chat-assistent (`editor_patch`) in het formulier/regelraster zetten.
   * Niets wordt opgeslagen: het product wordt dirty en de gebruiker controleert en slaat zelf op.
   * Secties van de verkoopomschrijving gaan via dezelfde weg als "Voorstel met AI" (alleen gevulde
   * secties overschrijven); de werkplaatsinstructie wordt opnieuw samengevoegd met de omschrijving.
   */
  function applyChatPatch(patch: ChatEditorPatch): { applied: string[]; skipped: string[] } {
    const applied: string[] = [];
    const skipped: string[] = [];
    if (!product.value || product.value.id !== patch.product_id)
      return { applied, skipped: ['product staat niet open'] };

    const f = patch.fields ?? {};
    const formPatch: Partial<ProductFormFields> = {};
    const str = (v: string | null | undefined) => v ?? '';
    for (const [key, value] of Object.entries(f)) {
      if (value === undefined) continue;
      switch (key) {
        case 'assembly_instruction':
          break; // hieronder samengevoegd met de omschrijving
        case 'partial_relocation_minimum':
          formPatch.partial_relocation_minimum = value == null ? '' : String(value);
          break;
        case 'cost_price':
          formPatch.cost_price = value == null ? '' : String(value);
          break;
        case 'use_partial_relocation':
        case 'use_component_partial':
        case 'apply_sub_assembly_end_product':
        case 'apply_sub_assembly_component':
          formPatch[key] = (value ?? '') as '' | 'J' | 'N';
          break;
        case 'name':
        case 'unit_code':
          formPatch[key] = String(value ?? '');
          break;
        case 'sizing_input':
          // alleen de opgegeven sizing-velden; de rest van de huidige invoer blijft staan
          formPatch.sizing_input = mergeSizingPatch(form.sizing_input, f.sizing_input ?? null);
          break;
        case 'search_name':
        case 'proposed_product_code':
        case 'long_description_nl':
        case 'long_description_en':
        case 'notes':
        case 'journal_entry_code':
        case 'warehouse_code':
        case 'location_code':
        case 'ean_code':
        case 'assembly_warehouse_code':
          formPatch[key] = str(value as string | null);
          break;
        default:
          skipped.push(`onbekend veld ${key}`);
          continue;
      }
      applied.push(CHAT_FIELD_LABELS[key] ?? key);
    }

    if (patch.sales_description) {
      const { lang, suggestion } = patch.sales_description;
      const key = lang === 'nl' ? 'long_description_nl' : 'long_description_en';
      const current = formPatch[key] ?? form[key];
      const r = applySuggestion(parseSalesDescription(current).model, suggestion);
      if (r.changed.length) {
        formPatch[key] = serializeSalesDescription(r.model, lang);
        applied.push(`verkoopomschrijving ${lang.toUpperCase()} (${r.changed.join(', ')})`);
      } else skipped.push(`verkoopomschrijving ${lang.toUpperCase()}: geen nieuwe secties`);
    }

    // Werkplaatsinstructie = instructiedeel + lange omschrijving (NL, anders EN)
    const instructionChanged = f.assembly_instruction !== undefined;
    if (
      instructionChanged ||
      formPatch.long_description_nl !== undefined ||
      formPatch.long_description_en !== undefined
    ) {
      const body = instructionChanged
        ? (f.assembly_instruction ?? '')
        : splitAssemblyInstruction(form.assembly_instruction).instruction;
      const nl = formPatch.long_description_nl ?? form.long_description_nl;
      const en = formPatch.long_description_en ?? form.long_description_en;
      formPatch.assembly_instruction = mergeAssemblyInstruction(body, pickLongDescription(nl, en));
      if (instructionChanged) applied.push(CHAT_FIELD_LABELS.assembly_instruction);
    }
    if (Object.keys(formPatch).length) updateProduct(formPatch);

    for (const op of patch.line_ops ?? []) {
      if (op.op === 'add') {
        const after = op.after_line_id ? lines.value.findIndex((l) => l.id === op.after_line_id) : -1;
        addLine(after >= 0 ? after : undefined, {
          description: op.description,
          unit4_product_code: op.unit4_product_code ?? null,
          unit_code: op.unit_code ?? product.value.unit_code ?? 'STK',
          qty_in_composition: op.qty_in_composition,
          composition_per: op.composition_per,
          percentage_loss: op.percentage_loss,
          child_product_id: op.child_product_id ?? null,
          is_composite: !!op.child_product_id,
          new_article: null,
          status: op.unit4_product_code || op.child_product_id ? 'linked' : 'provisional',
          notes: op.notes ?? null,
        });
        applied.push(`regel toegevoegd: ${op.description}`);
        continue;
      }
      const idx = lines.value.findIndex((l) => l.id === op.line_id);
      if (idx === -1) {
        skipped.push(`regel ${op.line_id} niet gevonden`);
        continue;
      }
      if (op.op === 'remove') {
        removeLine(idx);
        applied.push(`regel ${idx + 1} verwijderd`);
      } else {
        const { op: _op, line_id: _id, ...rest } = op;
        const linePatch: Partial<EditableLine> = {};
        for (const [k, v] of Object.entries(rest))
          if (v !== undefined) (linePatch as Record<string, unknown>)[k] = v;
        updateLine(idx, linePatch);
        applied.push(`regel ${idx + 1} gewijzigd`);
      }
    }
    return { applied, skipped };
  }

  // ---------- Opslaan ----------

  /**
   * Save-flow: (1) productvelden via PUT /products/:id als die gewijzigd zijn (versie loopt op),
   * (2) regels via PUT /products/:id/lines met de laatst bekende versie. Bij 409 VERSION_MISMATCH
   * wordt `conflictVersion` gezet en een toast getoond; lokale wijzigingen blijven staan.
   */
  async function save(): Promise<boolean> {
    if (!product.value || saving.value) return false;
    if (!dirty.value) return true;
    if (!isValid.value) {
      const first = validationErrors.value[0];
      const msg = first ? `Regel ${first.index + 1}: ${first.message}` : Object.values(formErrors.value)[0];
      toastService.warning(msg ?? 'Controleer de invoer');
      return false;
    }
    saving.value = true;
    const id = product.value.id;
    try {
      let currentVersion = product.value.version;
      let updatedProduct = product.value;

      if (productDirty.value) {
        const p = await productService.update(
          id,
          { version: currentVersion, ...formToBody(form) },
          { noToast: true },
        );
        currentVersion = p.version;
        updatedProduct = { ...product.value, ...p, lines: product.value.lines };
        // tussenstand vastleggen zodat een fout in stap 2 de versie niet verouderd achterlaat
        product.value = updatedProduct;
      }

      if (linesDirty.value) {
        const result = await productService.replaceLines(id, currentVersion, lines.value.map(toLineInput), {
          noToast: true,
        });
        applyServerState(result);
      } else {
        // alleen productvelden gewijzigd — status/velden zijn al bijgewerkt via `p`
        applyServerState({ ...updatedProduct, lines: product.value.lines });
      }
      lastSavedAt.value = new Date();
      toastService.success('Opgeslagen');
      return true;
    } catch (e) {
      if (isVersionMismatch(e)) {
        const details = (e as any).response?.data?.error?.details;
        conflictVersion.value =
          typeof details?.currentVersion === 'number'
            ? details.currentVersion
            : typeof details?.version === 'number'
              ? details.version
              : -1;
        toastService.error(
          'Iemand anders heeft dit product gewijzigd — herlaad het product om verder te gaan.',
        );
      } else {
        toastService.error(extractErrorMessage(e, 'Opslaan mislukt'));
      }
      return false;
    } finally {
      saving.value = false;
    }
  }

  // ---------- Unit4-koppeling en composite ----------

  /**
   * Koppelt regel `idx` aan Unit4-product `code`. Opgeslagen regels via POST /lines/:id/link;
   * nieuwe (nog niet opgeslagen) regels worden lokaal ingevuld via GET /unit4/products/:code.
   */
  async function linkLine(idx: number, code: string, unit?: string | null): Promise<boolean> {
    const l = lines.value[idx];
    if (!l) return false;
    const trimmed = code.trim();
    if (!trimmed) return false;
    try {
      if (l.id) {
        const res = await productService.linkLine(l.id, trimmed, unit ?? l.unit_code);
        Object.assign(l, {
          unit4_product_code: res.line.unit4_product_code,
          unit4_description: res.line.unit4_description,
          unit_code: res.line.unit_code,
          status: res.line.status,
          validation_errors: res.line.validation_errors,
        });
        if (!l.description.trim() && res.line.description) l.description = res.line.description;
        // server-staat vernieuwen (versie/status), lokale edits blijven
        await refreshOriginal();
        if (res.substituted)
          toastService.warning(`Unit4 heeft ${trimmed} vervangen door ${res.line.unit4_product_code}`);
        else toastService.success(`Gekoppeld aan ${res.line.unit4_product_code}`);
      } else {
        const u = await unit4Service.get(trimmed, unit ?? l.unit_code, { noToast: true });
        l.unit4_product_code = u.productCode;
        l.unit4_description = u.description ?? null;
        if (u.unitCode) l.unit_code = u.unitCode;
        if (!l.description.trim() && u.description) l.description = u.description;
        l.status = 'linked';
        toastService.success(`Gekoppeld aan ${u.productCode} (wordt definitief bij opslaan)`);
      }
      return true;
    } catch (e) {
      const notFound = axios.isAxiosError(e) && e.response?.status === 404;
      toastService.error(
        notFound
          ? `Productcode ${trimmed} niet gevonden in Unit4`
          : extractErrorMessage(e, 'Koppelen mislukt'),
      );
      return false;
    }
  }

  /**
   * Maakt van regel `idx` een composite-regel met een nieuw sub-product `name`.
   * Slaat eerst op als er wijzigingen zijn (regel moet een id hebben). Retourneert child_product_id.
   */
  async function makeComposite(idx: number, name: string): Promise<number | null> {
    if (!product.value) return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    const key = lines.value[idx]?.key;
    // Een nieuwe regel zonder omschrijving zou de save blokkeren ("Omschrijving is verplicht");
    // de naam van het sub-product is dan meteen ook de regelomschrijving.
    const current = lines.value[idx];
    if (current && !current.description.trim()) current.description = trimmed;
    if (dirty.value || !lines.value[idx]?.id) {
      const ok = await save();
      if (!ok) return null;
    }
    // na save kan de index verschoven zijn — zoek op key of positie
    const line = lines.value.find((l) => l.key === key) ?? lines.value[idx];
    if (!line?.id) {
      toastService.error('Regel kon niet worden opgeslagen');
      return null;
    }
    try {
      const res = await productService.makeComposite(line.id, { name: trimmed });
      await load(product.value.id, { silent: true });
      toastService.success(`Genest sub-product "${trimmed}" aangemaakt`);
      return res.child_product_id;
    } catch {
      return null;
    }
  }

  /** Bestaand product als sub-product koppelen aan regel `idx`. */
  async function linkExistingChild(idx: number, childProductId: number): Promise<number | null> {
    if (!product.value) return null;
    const key = lines.value[idx]?.key;
    if (dirty.value || !lines.value[idx]?.id) {
      const ok = await save();
      if (!ok) return null;
    }
    const line = lines.value.find((l) => l.key === key) ?? lines.value[idx];
    if (!line?.id) return null;
    try {
      const res = await productService.makeComposite(line.id, { child_product_id: childProductId });
      await load(product.value.id, { silent: true });
      return res.child_product_id;
    } catch {
      return null;
    }
  }

  return {
    // state
    product,
    lines,
    form,
    loading,
    saving,
    loadError,
    conflictVersion,
    lastSavedAt,
    // computed
    productId,
    version,
    original,
    dirty,
    linesDirty,
    productDirty,
    validationErrors,
    formErrors,
    isValid,
    stats,
    // actions
    load,
    refreshOriginal,
    discard,
    clear,
    addLine,
    removeLine,
    moveLine,
    updateLine,
    duplicateLine,
    updateProduct,
    applyChatPatch,
    save,
    linkLine,
    makeComposite,
    linkExistingChild,
    applyServerState,
  };
});
